/**
 * The claim filters, EXACT: each person's figures added up over the claim cells the
 * filter selects, instead of scaled by their share of it.
 *
 * Review #16 (Shayna, 2026-10-02; "what needs to be done to get this exact?",
 * 2026-10-07). A claim type, service type or claim status describes a claim, while the
 * bundle's rows describe a person, so until the bundle carried these cells the filters
 * reweighted every person by their claim mix (`modeledWeight` in filters.tsx) — exact
 * for billed claims and claim labor, scaled for everything else, and badged M.
 *
 * `scripts/build_bundle.py` (`claim_cells`) now splits, per person, every measure those
 * filters touch by (claim type, service type, claim status):
 *
 *  - month rows: billed claims, claim labor, resolved claims (in the month they
 *    resolved), the three touch buckets and their touches, all touches, the open pile
 *    and how much of it was worked;
 *  - the collection series, per bill month: charge and resolved charge;
 *  - the worklist snapshot: the pile, how much of it they worked, and its age buckets;
 *  - facilities, as a per-person id with a bitmask of the months worked in each cell,
 *    so a distinct count can be taken over any cells and any months.
 *
 * Every cell set, all ticked, adds up to the unfiltered figure (the builder logs the
 * check). So a filtered person is rebuilt here exactly as `derive` builds them — the
 * same sums, the same ratios of sums — over the cells picked. Nothing is scaled except
 * the Weighted claims switch's adjustment, which /api/weights returns per person-month
 * and not per claim type: under a claim filter that one figure still takes each month's
 * billed share, and stays badged (`claimFilterNote`).
 *
 * The score does not follow the claim filters, on either path (MEASURE-RULES §10.5).
 */
import type { Person } from './selectors'
import type { ClaimCellDict, PersonCells, TouchMonthRow } from './types'
import type { Filters } from './filters'
import { divide, touchBase } from '@/measures/core'
import { collectionRateOf, windowCollection, type CollectionSeries } from '@/measures/collection'

/** The parsed cells: the dictionary, and each person's rows by name. */
export interface ClaimSplit {
  dict: ClaimCellDict
  byName: Map<string, PersonCells>
}

/** The bundle's cells, or null when it predates them — the filters then stay modelled. */
export function claimSplitOf(d: {
  claimCells?: ClaimCellDict
  employees: readonly { employee: string; cells?: PersonCells }[]
}): ClaimSplit | null {
  if (!d.claimCells) return null
  const byName = new Map<string, PersonCells>()
  for (const e of d.employees) if (e.cells) byName.set(e.employee, e.cells)
  return { dict: d.claimCells, byName }
}

/** A multi-select matches when it is empty (all) or contains the value. */
const picks = (sel: readonly string[], v: string) => sel.length === 0 || sel.includes(v)

/** Which cells the claim filters select, by cell index. */
export function selectedCells(
  dict: Pick<ClaimCellDict, 'cells'>,
  f: Pick<Filters, 'claimType' | 'serviceType' | 'claimStatus'>,
): boolean[] {
  return dict.cells.map(
    ([type, service, status]) =>
      picks(f.claimType, type) && picks(f.serviceType, service) && picks(f.claimStatus, status),
  )
}

/** What `exactPerson` needs to know about the range, off `Derived`. */
export interface RangeContext {
  /** The months in the selected range. */
  months: readonly string[]
  /** Every month the bundle carries. */
  allMonths: readonly string[]
}

/** Bit `i` of a facility's month mask. Masks reach past 32 bits, so no `&`. */
const hasBit = (mask: number, i: number) => Math.floor(mask / 2 ** i) % 2 === 1

const at = (row: readonly number[], i: number) => row[i] ?? 0

/**
 * One person rebuilt over the selected cells: their month rows, collection series,
 * worklist and facilities, and every figure `derive` takes off them. Pay, hours and
 * the score are the person's, not a claim's, and are left as they are.
 *
 * A person with no cells has nothing in any cell: zero in every claim measure.
 */
export function exactPerson(
  p: Person,
  cells: PersonCells | undefined,
  dict: ClaimCellDict,
  sel: readonly boolean[],
  range: RangeContext,
): Person {
  const months = exactMonths(p.months, cells, dict, sel)
  const collectionMonths = exactSeries(cells, dict, sel)
  const facilities = exactFacilities(cells, dict, sel, range.months)

  const sum = (get: (m: TouchMonthRow) => number) => months.reduce((a, m) => a + get(m), 0)
  const standardClaims = sum((m) => m.billedClaims)
  const weightedClaims =
    p.weightedClaims === null ? null : sum((m) => m.weightedClaims ?? m.billedClaims)
  const counted = weightedClaims ?? standardClaims
  const claimLabor = sum((m) => m.claimLabor)
  const resolvedClaims = sum((m) => m.resolvedClaims)
  const noTouch = sum((m) => m.resolvedNoTouch)
  const touched = sum(touchBase)
  const openPileSum = sum((m) => m.openClaims)
  const openWorkedSum = sum((m) => m.openClaimsWorked)

  // The range's collection window, as `derive` reads it.
  const first = range.months[0] ?? range.allMonths[0]
  const last = range.months[range.months.length - 1] ?? range.allMonths[range.allMonths.length - 1]
  const collection =
    first && last && p.collectionMonths ? windowCollection(collectionMonths, first, last) : null

  // Touches per claim has no monthly form, so `derive` blanks it under a range.
  const narrowed = range.months.length < range.allMonths.length
  const allTouches = monthSums(cells, dict, sel, new Set(p.months.map((m) => m.month)), 'allTouches')

  const wl = worklist(cells, dict, sel)

  return {
    ...p,
    months,
    collectionMonths: p.collectionMonths ? collectionMonths : undefined,
    claims: counted,
    standardClaims,
    weightedClaims,
    claimLabor,
    costPerClaim: divide(claimLabor, counted),
    resolvedClaims,
    // UNCAPPED, on billed claims whatever the Claims switch says — as `derive`.
    closedClaimPct: divide(resolvedClaims, standardClaims),
    noTouch,
    touchBase: touched,
    zeroTouchShare: divide(noTouch, touched),
    touchesPerClaim: narrowed || p.touchesPerClaim === null ? null : divide(allTouches, standardClaims),
    billedPerMonth: divide(counted, p.months.length || range.allMonths.length),
    collection,
    collectedPct: collectionRateOf(collection),
    openPileSum,
    openWorkedSum,
    openWorkedPct: divide(openWorkedSum, openPileSum),
    openClaims: wl.open,
    notWorkedThisMonth: Math.max(0, wl.open - wl.worked),
    age: p.age === null ? null : { open: wl.open, ...wl.age },
    facilities,
    // Who someone who has left is ranked as: the same cells, over their active months.
    ...(p.rankedAs ? { rankedAs: exactPerson(p.rankedAs, cells, dict, sel, range) } : {}),
  }
}

/** Field sums over the selected cells, per month index. */
function cellMonths(
  cells: PersonCells | undefined,
  dict: ClaimCellDict,
  sel: readonly boolean[],
): Map<string, number[]> {
  const out = new Map<string, number[]>()
  const n = dict.monthFields.length
  for (const r of cells?.m ?? []) {
    if (!sel[r[1]!]) continue
    const month = dict.months[r[0]!]!
    let acc = out.get(month)
    if (!acc) out.set(month, (acc = new Array<number>(n).fill(0)))
    for (let i = 0; i < n; i++) acc[i]! += at(r, i + 2)
  }
  return out
}

/** One field summed over the selected cells and the given months. */
function monthSums(
  cells: PersonCells | undefined,
  dict: ClaimCellDict,
  sel: readonly boolean[],
  months: ReadonlySet<string>,
  field: string,
): number {
  const i = dict.monthFields.indexOf(field)
  if (i < 0) return 0
  let s = 0
  for (const r of cells?.m ?? [])
    if (sel[r[1]!] && months.has(dict.months[r[0]!]!)) s += at(r, i + 2)
  return s
}

/**
 * The person's month rows over the selected cells. Every claim measure is the cells'
 * sum; facilities is the distinct count of the month's facilities in those cells. Pay
 * and hours stay. The month's own collection window is read off the exact series.
 *
 * Two things are not in the cells and keep a share — the month's billed claims in the
 * cells over all its billed claims: the Weighted switch's adjustment (see the module
 * note), and the deprecated FactClaimTouches buckets nothing reads any more.
 */
export function exactMonths(
  rows: readonly TouchMonthRow[],
  cells: PersonCells | undefined,
  dict: ClaimCellDict,
  sel: readonly boolean[],
): TouchMonthRow[] {
  const sums = cellMonths(cells, dict, sel)
  const field = (s: number[] | undefined, name: string) => {
    const i = dict.monthFields.indexOf(name)
    return s && i >= 0 ? s[i]! : 0
  }
  const series = exactSeries(cells, dict, sel)
  const fac = facilitiesByMonth(cells, dict, sel)
  return rows.map((m) => {
    const s = sums.get(m.month)
    const billed = field(s, 'billedClaims')
    const labor = field(s, 'claimLabor')
    const resolved = field(s, 'resolvedClaims')
    const share = m.billedClaims === 0 ? 0 : billed / m.billedClaims
    const coll = windowCollection(series, m.month, m.month)
    const had = m.charge != null || m.collectedPct != null
    return {
      ...m,
      billedClaims: billed,
      claimLabor: labor,
      resolvedClaims: resolved,
      costPerResolvedClaim: resolved === 0 ? 0 : labor / resolved,
      resolvedNoTouch: field(s, 'resolvedNoTouch'),
      resolvedOneTouch: field(s, 'resolvedOneTouch'),
      resolvedTwoPlusTouch: field(s, 'resolvedTwoPlusTouch'),
      resolvedTouches: field(s, 'resolvedTouches'),
      // Past the department's age limit (data/agedNoTouch.ts); 0 where none applies.
      resolvedNoTouchAged: field(s, 'resolvedNoTouchAged'),
      openClaims: field(s, 'openClaims'),
      openClaimsWorked: field(s, 'openClaimsWorked'),
      facilities: fac.get(m.month) ?? 0,
      weightedClaims:
        m.weightedClaims == null ? undefined : billed + (m.weightedClaims - m.billedClaims) * share,
      noTouch: m.noTouch * share,
      oneTouch: m.oneTouch * share,
      twoPlusTouch: m.twoPlusTouch * share,
      claimsTouched: m.claimsTouched * share,
      collectedPct: had ? collectionRateOf(coll) : m.collectedPct,
      charge: had ? (coll?.total ?? null) : m.charge,
      chargeResolved: had ? (coll?.resolved ?? null) : m.chargeResolved,
    }
  })
}

/** Bill month → [charge, resolved charge] over the selected cells. */
export function exactSeries(
  cells: PersonCells | undefined,
  dict: ClaimCellDict,
  sel: readonly boolean[],
): CollectionSeries {
  const out: Record<string, [number, number]> = {}
  for (const r of cells?.c ?? []) {
    if (!sel[r[1]!]) continue
    const month = dict.months[r[0]!]!
    const acc = (out[month] ??= [0, 0])
    acc[0] += at(r, 2)
    acc[1] += at(r, 3)
  }
  return out
}

/** Distinct facilities over the selected cells, in any of `months`. */
function exactFacilities(
  cells: PersonCells | undefined,
  dict: ClaimCellDict,
  sel: readonly boolean[],
  months: readonly string[],
): number {
  const bits = months.map((m) => dict.months.indexOf(m)).filter((i) => i >= 0)
  const seen = new Set<number>()
  for (const r of cells?.f ?? []) {
    if (!sel[r[1]!] || seen.has(r[0]!)) continue
    if (bits.some((b) => hasBit(r[2]!, b))) seen.add(r[0]!)
  }
  return seen.size
}

/** Distinct facilities per month over the selected cells. */
function facilitiesByMonth(
  cells: PersonCells | undefined,
  dict: ClaimCellDict,
  sel: readonly boolean[],
): Map<string, number> {
  const sets = new Map<string, Set<number>>()
  for (const r of cells?.f ?? []) {
    if (!sel[r[1]!]) continue
    dict.months.forEach((m, i) => {
      if (!hasBit(r[2]!, i)) return
      let s = sets.get(m)
      if (!s) sets.set(m, (s = new Set()))
      s.add(r[0]!)
    })
  }
  return new Map([...sets].map(([m, s]) => [m, s.size]))
}

/** The worklist snapshot over the selected cells. */
function worklist(cells: PersonCells | undefined, dict: ClaimCellDict, sel: readonly boolean[]) {
  const ix = (name: string) => dict.worklistFields.indexOf(name) + 1
  const got = (r: readonly number[], name: string) => (ix(name) > 0 ? at(r, ix(name)) : 0)
  const t = { open: 0, worked: 0, age: { neverWorked: 0, last14: 0, from15to30: 0, over30: 0 } }
  for (const r of cells?.w ?? []) {
    if (!sel[r[0]!]) continue
    t.open += got(r, 'openClaims')
    t.worked += got(r, 'worked')
    t.age.neverWorked += got(r, 'neverWorked')
    t.age.last14 += got(r, 'workedLast14')
    t.age.from15to30 += got(r, 'worked15to30')
    t.age.over30 += got(r, 'workedOver30')
  }
  return t
}

/**
 * The hover on the claim filters' M, and the line under the filter bar: what a claim
 * filter still models, named. Null when nothing does — no claim filter, or the bundle
 * carries the cells and the Claims switch is on Standard.
 */
export function claimFilterNote(state: {
  filtering: boolean
  exact: boolean
  weighted: boolean
}): string | null {
  if (!state.filtering) return null
  if (!state.exact) return MODELLED_ALL
  if (state.weighted) return MODELLED_WEIGHTED
  return null
}

/** An older bundle, without the cells: today's note, unchanged. */
export const MODELLED_ALL =
  'Modelled: this filter reweights each person by their claim mix. Billed claims, claim labor and cost per claim are exact; resolved claims, touches, facilities, collection and the open pile are scaled by each person’s share.'

/** The cells, with the Weighted claims switch on. */
export const MODELLED_WEIGHTED =
  'Modelled under this filter: weighted claims only. The claim rules’ adjustment comes per person-month, not per claim type, so it is split by each month’s billed-claim share. Every other figure is exact.'
